import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FolderOpen, FileText, Clock, BarChart3 } from "lucide-react";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import DashboardHero from "@/components/dashboard/DashboardHero";
import KpiCard from "@/components/dashboard/KpiCard";
import AttentionFeed from "@/components/dashboard/AttentionFeed";
import EnquiryPipeline from "@/components/dashboard/EnquiryPipeline";
import RecentProjects from "@/components/dashboard/RecentProjects";
import FollowUpQueue from "@/components/dashboard/FollowUpQueue";
import AddEnquiryDialog from "@/components/enquiries/AddEnquiryDialog";

export default function DashboardPage() {
  const { user, hasPermission } = useAuth();
  const [enquiryDialogOpen, setEnquiryDialogOpen] = useState(false);
  const canSeeEnquiries = hasPermission("enquiry:manage");
  const canSeeProjects = hasPermission("project:manage");

  const { data: summary } = useQuery({ queryKey: ["dashboard-summary"], queryFn: () => client.get("/dashboard/summary").then(unwrap) });
  const { data: attention = [] } = useQuery({ queryKey: ["dashboard-attention"], queryFn: () => client.get("/dashboard/attention").then(unwrap) });
  const { data: enquiries = [] } = useQuery({
    queryKey: ["enquiries"],
    queryFn: () => client.get("/enquiries").then(unwrap),
    enabled: canSeeEnquiries,
  });
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => client.get("/projects").then(unwrap),
    enabled: canSeeProjects,
  });

  const firstName = useMemo(() => user?.name?.split(" ")?.[0] || "there", [user]);
  const pendingApprovals = summary?.pendingApprovals?.count;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
      <DashboardHero firstName={firstName} onNewEnquiry={() => setEnquiryDialogOpen(true)} />

      {/* KPI row overlaps the hero's lower edge on wide screens. */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6 xl:-mt-14 xl:px-4">
        <KpiCard
          testId="kpi-active-projects"
          to="/projects"
          icon={FolderOpen}
          tone="green"
          label="Active Projects"
          value={summary?.activeProjects?.count ?? "—"}
          trendPct={summary?.activeProjects?.trendPct}
        />
        <KpiCard
          testId="kpi-quotations"
          to="/quotations"
          icon={FileText}
          tone="terracotta"
          label="Quotations"
          value={summary?.quotations?.count ?? "—"}
          sublabel={`${formatINR(summary?.quotations?.value)} in value`}
        />
        <KpiCard
          testId="kpi-pending-approvals"
          to="/quotations"
          icon={Clock}
          tone="danger"
          label="Pending Approvals"
          value={pendingApprovals ?? "—"}
          sublabel={pendingApprovals > 0 ? "Need your review" : "All clear"}
          sublabelTone={pendingApprovals > 0 ? "text-danger" : "text-success"}
        />
        <KpiCard
          testId="kpi-month-sales"
          to="/quotations"
          icon={BarChart3}
          tone="green"
          label="This Month Sales"
          value={formatINR(summary?.thisMonthSales?.amount)}
          trendPct={summary?.thisMonthSales?.trendPct}
        />
      </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-6 min-w-0">
          <AttentionFeed items={attention} />
          {canSeeProjects && <RecentProjects projects={projects} />}
        </div>
        <div className="xl:col-span-4 space-y-6 min-w-0">
          {canSeeEnquiries && <EnquiryPipeline enquiries={enquiries} />}
          <FollowUpQueue items={attention} />
        </div>
      </div>

      <AddEnquiryDialog open={enquiryDialogOpen} setOpen={setEnquiryDialogOpen} />
    </div>
  );
}
